import React, { useState } from "react";
import "../../styles/index.less";

// Define types
interface FAQItem {
  question: string;
  answer: string;
}

interface FAQData {
  REITs: FAQItem[];
  InvITs: FAQItem[];
}

interface FaqProps {
  activeTab: "REITs" | "InvITs";
  setActiveTab: (tab: "REITs" | "InvITs") => void;
}

const Faq: React.FC<FaqProps> = ({ activeTab, setActiveTab }) => {
  // Accordion state
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // FAQ data with proper typing
  const faqs: FAQData = {
    REITs: [
      {
        question: "What are REITs?",
        answer: `
          Real Estate Investment Trusts (REITs) are vehicles that own and operate income generating real estate properties. They enable individual and retail investors to invest in the real estate sector in the form of a financial instrument, without the associated complexities of having to buy or manage any property directly. REITs are required to distribute majority of their income as dividends and are often traded on stock exchanges, offering liquidity like stocks. `,
      },
      {
        question: "Why invest in REITs?",
        answer: `
        Investing in REITs offers: 
        <ul>
  <li><strong>Regular income:</strong> REITs distribute most of their rental income as dividends.</li>
  <li><strong>Diversification:</strong> Exposure to real estate helps balance an investment portfolio.</li>
  <li><strong>Liquidity:</strong> Listed REITs trade on stock exchanges like regular shares.</li>
  <li><strong>Professional management:</strong> Assets are managed by experienced real estate professionals.</li>
  <li><strong>Inflation hedge:</strong> Rental income tends to rise with inflation.</li>
</ul>`,
      },
      {
        question: "How do REITs work? ",
        answer: `
REITs (Real Estate Investment Trusts) pool money from investors to purchase, operate, and manage income-generating real estate such as offices, malls, hotels, and warehouses. 

The rental income collected from tenants is distributed among investors as dividends, while the value of REITs units can also appreciate over time based on property performance.

In India, REITs are regulated by SEBI, ensuring transparency and investor protection. 
  `,
      },
      {
        question: "How do REITs generate income? ",
        answer: `
REITs earn income mainly from: 

<ul>
  <li>Rental income from leased commercial properties (like offices and malls).</li>
  <li>Capital appreciation from property value increases over time. </li>
  <li>Interest income (in the case of mortgage REITs that lend to developers). </li>
</ul>
  `,
      },
      {
        question: "What are the benefits of investing in REITs?",
        answer: `
        Investing in REITs offers several advantages: 
        <ul>
  <li><strong>Regular income:</strong> Consistent dividend payouts from rental earnings.</li>
  <li><strong>Portfolio diversification:</strong> Exposure to real estate without buying physical property.</li>
  <li><strong>Professional management:</strong> Managed by experts in real estate and finance.</li>
  <li><strong>Liquidity:</strong> Listed REITs can be easily traded on stock exchanges.</li>
  <li><strong>Inflation hedge:</strong> Rental income often increases with inflation, protecting purchasing power.</li>
  <li><strong>Lower entry cost:</strong> Investors can participate in large real estate assets with small amounts.</li>
</ul>`,
      },
      {
        question: "What is the taxation on REITs investments?",
        answer: `
    <ul>
      <li><strong>Interest Income:</strong> Taxable at applicable income tax rates. Withholding tax is deducted by the REITs (5% for non-residents, 10% for resident investors).</li>
      <li><strong>Dividend Exemption:</strong> Tax-exempt in the hands of unitholders, provided the underlying SPVs (Special Purpose Vehicles) have opted for the old tax regime.</li>
      <li>
        <strong>Capital Gains:</strong> Taxable on an on-market sale of REITs units —
        <ul>
          <li><strong>Long-Term Capital Gains (LTCG):</strong> 12.5% (if held for more than 36 months).</li>
          <li><strong>Short-Term Capital Gains (STCG):</strong> 20% (if held for less than 36 months). For non-residents, rates are subject to the applicable <strong>Double Taxation Avoidance Agreement (DTAA)</strong>.</li>
        </ul>
      </li>
      <li><strong>Capital Repayment:</strong> Non-taxable until the cumulative capital repayment exceeds the cost of acquisition, after which the excess amount is taxed at the investor’s <strong>marginal tax rate</strong>.</li>
    </ul>
  `,
      },
    ],
    InvITs: [
      {
        question: "What are InvITs?",
        answer:
          "Infrastructure Investment Trusts (InvITs) are investment vehicles that pool funds from multiple investors across categories to invest in operational infrastructure assets such as highways, power plants, and warehouses. Similar to other investment instruments, they offer regular income through distributions and potential long-term capital appreciation. ",
      },
      {
        question: "Why invest in InvITs?",
        answer:
          "Investing in InvITs offers stable income streams, portfolio diversification, professional management, potential for capital appreciation, and transparency. They provide access to high-quality infrastructure assets, act as an inflation hedge, making them an attractive investment option. ",
      },
      {
        question: "How do InvITs work?",
        answer: `
InvITs pool capital from investors to invest in and manage a portfolio of infrastructure assets. These assets typically generate stable cash flows, which are distributed to investors in the form of dividends. The InvIT structure allows investors to benefit from long-term infrastructure investments, which are typically capital-intensive and require expertise to manage.

InvITs are regulated entities governed by the Securities and Exchange Board of India (Infrastructure Investment Trusts) Regulations, 2014, which ensures high standards of transparency and corporate governance in the infrastructure investment landscape.
  `,
      },

      {
        question: "How do InvITs generate income for investors?",
        answer: `
InvITs generate income from the assets they hold. This income could come from:

<ul>
  <li>Toll collections on highways and roads.</li>
  <li>Usage fees for power transmission lines or telecom infrastructure.</li>
  <li>Revenue-sharing contracts from renewable energy projects.</li>
</ul>
  `,
      },

      {
        question: "What are the benefits of investing in InvITs?",
        answer: `
<ul>
  <li><strong>Stable Returns:</strong> They distribute a significant portion of their income as dividends, providing a steady income stream.</li>
  <li><strong>Diversification:</strong> InvITs allow investors to diversify their portfolios by adding infrastructure assets, which typically have low correlation with traditional equity and debt investments.</li>
  <li><strong>Transparency:</strong> InvITs are required to comply with SEBI regulations, ensuring regular disclosures of their financial performance and asset details, providing transparency for investors.</li>
  <li><strong>Security:</strong> Low-risk investments offered to attract long-term investors, such as insurance and pension funds.</li>
</ul>
  `,
      },
      {
        question: "What is the taxation on InvIT investments?",
        answer: `
<ul>
  <li><strong>Interest Income:</strong> Taxable at applicable tax rates. Withholding tax to be deducted by the InvITs (5% for non-residents, 10% for others).</li>
  <li><strong>Dividend Exemption:</strong> No further tax is applicable on dividends received by unitholders, provided the SPVs are taxed under the old tax regime.</li>
  <li><strong>Capital Gains:</strong> Taxable on an on-market sale, with Long-Term Capital Gains (LTCG) at 12.5% and Short-Term Capital Gains (STCG) at 20%, subject to the taxability under the applicable Double Taxation Avoidance Agreement (DTAA) for non-residents.</li>
  <li><strong>Capital Repayment:</strong> Non-taxable until the cumulative capital repayment exceeds the cost of acquisition, after which it is taxed at marginal tax rates.</li>
</ul>
  `,
      },
    ],
  };

  // Accordion toggle
  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="faq-section">
      <h2 className="faq-title">
        We know that you’ve got a lot of questions <br />
        <span>We got answers</span>
      </h2>

      {/* FAQ Tabs */}
      <div className="faq-tabs">
        <button
          className={`tab ${activeTab === "REITs" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("REITs");
            setOpenIndex(null);
          }}
        >
          REITs
        </button>
        <button
          className={`tab ${activeTab === "InvITs" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("InvITs");
            setOpenIndex(null);
          }}
        >
          InvITs
        </button>
      </div>

      {/* Accordion */}
      <div className="faq-accordion">
        {faqs[activeTab].map((item, index) => (
          <div
            key={index}
            className={`faq-item ${openIndex === index ? "open" : ""}`}
            onClick={() => toggleAccordion(index)}
          >
            <div className="faq-question">
              {item.question}
              <span className={`faq-icon ${openIndex === index ? "open" : ""}`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#132c49"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </div>
            <div className={`faq-answer ${openIndex === index ? "open" : ""}`}>
              {item.answer.includes("<ul>") ? (
                <div dangerouslySetInnerHTML={{ __html: item.answer }} />
              ) : (
                item.answer
                  .trim()
                  .split("\n\n")
                  .map((para, idx) => <p key={idx}>{para}</p>)
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Faq;
